import React, { forwardRef, HTMLAttributes } from 'react';
import { useThemeStyles } from '../../hooks/useTheme';
import { ThemeTypography } from '../../types/theme';
import styles from './Typography.module.scss';
import { cx } from '../../utils';

// Typography variant types
export type DisplayVariant = keyof ThemeTypography['display'];
export type TextVariant = keyof ThemeTypography['text'];
export type TypographyVariant =
  | DisplayVariant
  | TextVariant
  | `display-${DisplayVariant}`
  | `text-${TextVariant}`;

// Typography element types
export type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'a'
  | 'label'
  | 'caption'
  | 'strong'
  | 'em'
  | 'small'
  | 'mark'
  | 'del'
  | 'ins'
  | 'sub'
  | 'sup'
  | 'code'
  | 'pre'
  | 'blockquote';

// Color variants
export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'disabled'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'inherit'
  | 'brand'
  | 'brand-25'
  | 'brand-50'
  | 'brand-100'
  | 'brand-200'
  | 'brand-300'
  | 'brand-400'
  | 'brand-500'
  | 'brand-600'
  | 'brand-700'
  | 'brand-800'
  | 'brand-900'
  | 'brand-950'
  | 'tertiary-25'
  | 'tertiary-50'
  | 'tertiary-100'
  | 'tertiary-200'
  | 'tertiary-300'
  | 'tertiary-400'
  | 'tertiary-500'
  | 'tertiary-600'
  | 'tertiary-700'
  | 'tertiary-800'
  | 'tertiary-900'
  | 'tertiary-950'
  | 'grayDark-25'
  | 'grayDark-50'
  | 'grayDark-100'
  | 'grayDark-200'
  | 'grayDark-300'
  | 'grayDark-400'
  | 'grayDark-500'
  | 'grayDark-600'
  | 'grayDark-700'
  | 'grayDark-800'
  | 'grayDark-900'
  | 'grayDark-950'
  | 'light-primary'
  | 'light-secondary'
  | 'light-disabled';

// Font weight variants
export type TypographyWeight = keyof ThemeTypography['fontWeight'];

// Font family variants
export type TypographyFamily = keyof ThemeTypography['fontFamily'];

// Text alignment
export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';

// Text transform
export type TypographyTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

// Text decoration
export type TypographyDecoration = 'none' | 'underline' | 'line-through' | 'overline';

// Responsive props
export interface ResponsiveProp<T> {
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  '2xl'?: T;
}

// Main Typography props interface
export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  // Content
  children?: React.ReactNode;

  // Typography variant
  variant?: TypographyVariant;

  // HTML element to render
  as?: TypographyElement;

  // Color
  color?: TypographyColor | ResponsiveProp<TypographyColor>;

  // Font weight
  weight?: TypographyWeight | ResponsiveProp<TypographyWeight>;

  // Font family
  family?: TypographyFamily | ResponsiveProp<TypographyFamily>;

  // Text alignment
  align?: TypographyAlign | ResponsiveProp<TypographyAlign>;

  // Text transform
  transform?: TypographyTransform | ResponsiveProp<TypographyTransform>;

  // Text decoration
  decoration?: TypographyDecoration | ResponsiveProp<TypographyDecoration>;

  // Custom styling
  className?: string;
  style?: React.CSSProperties;

  // Utility props
  truncate?: boolean;
  noWrap?: boolean;
  selectable?: boolean;

  // Spacing
  margin?: string | ResponsiveProp<string>;
  padding?: string | ResponsiveProp<string>;

  // Custom color (overrides theme color)
  customColor?: string;

  // Line height override
  lineHeight?: string | ResponsiveProp<string>;

  // Letter spacing override
  letterSpacing?: string | ResponsiveProp<string>;

  // Font size override
  fontSize?: string | ResponsiveProp<string>;
}

// Helper function to get responsive class names
const getResponsiveClasses = <T extends string>(
  prop: T | ResponsiveProp<T> | undefined,
  prefix: string
): string => {
  if (!prop) return '';

  if (typeof prop === 'string') {
    return `${prefix}-${prop}`;
  }

  const classes: string[] = [];
  Object.entries(prop).forEach(([breakpoint, value]) => {
    if (value) {
      classes.push(`${prefix}-${breakpoint}-${value}`);
    }
  });

  return classes.join(' ');
};

// Helper function to get color value from theme
const getColorValue = (
  color: TypographyColor,
  theme: ReturnType<typeof useThemeStyles>
): string => {
  switch (color) {
    case 'primary':
      return theme.colors.text.primary;
    case 'secondary':
      return theme.colors.text.secondary;
    case 'disabled':
      return theme.colors.text.disabled;
    case 'error':
      return theme.colors.error;
    case 'warning':
      return theme.colors.warning;
    case 'success':
      return theme.colors.success;
    case 'info':
      return theme.colors.info;
    case 'inherit':
      return 'inherit';
    case 'brand':
      return theme.colors.palettes.brand[500];
    case 'brand-25':
      return theme.colors.palettes.brand[25];
    case 'brand-50':
      return theme.colors.palettes.brand[50];
    case 'brand-100':
      return theme.colors.palettes.brand[100];
    case 'brand-200':
      return theme.colors.palettes.brand[200];
    case 'brand-300':
      return theme.colors.palettes.brand[300];
    case 'brand-400':
      return theme.colors.palettes.brand[400];
    case 'brand-500':
      return theme.colors.palettes.brand[500];
    case 'brand-600':
      return theme.colors.palettes.brand[600];
    case 'brand-700':
      return theme.colors.palettes.brand[700];
    case 'brand-800':
      return theme.colors.palettes.brand[800];
    case 'brand-900':
      return theme.colors.palettes.brand[900];
    case 'brand-950':
      return theme.colors.palettes.brand[950];
    case 'tertiary-25':
      return theme.colors.palettes.tertiary[25];
    case 'tertiary-50':
      return theme.colors.palettes.tertiary[50];
    case 'tertiary-100':
      return theme.colors.palettes.tertiary[100];
    case 'tertiary-200':
      return theme.colors.palettes.tertiary[200];
    case 'tertiary-300':
      return theme.colors.palettes.tertiary[300];
    case 'tertiary-400':
      return theme.colors.palettes.tertiary[400];
    case 'tertiary-500':
      return theme.colors.palettes.tertiary[500];
    case 'tertiary-600':
      return theme.colors.palettes.tertiary[600];
    case 'tertiary-700':
      return theme.colors.palettes.tertiary[700];
    case 'tertiary-800':
      return theme.colors.palettes.tertiary[800];
    case 'tertiary-900':
      return theme.colors.palettes.tertiary[900];
    case 'tertiary-950':
      return theme.colors.palettes.tertiary[950];
    case 'grayDark-25':
      return theme.colors.palettes.grayDark[25];
    case 'grayDark-50':
      return theme.colors.palettes.grayDark[50];
    case 'grayDark-100':
      return theme.colors.palettes.grayDark[100];
    case 'grayDark-200':
      return theme.colors.palettes.grayDark[200];
    case 'grayDark-300':
      return theme.colors.palettes.grayDark[300];
    case 'grayDark-400':
      return theme.colors.palettes.grayDark[400];
    case 'grayDark-500':
      return theme.colors.palettes.grayDark[500];
    case 'grayDark-600':
      return theme.colors.palettes.grayDark[600];
    case 'grayDark-700':
      return theme.colors.palettes.grayDark[700];
    case 'grayDark-800':
      return theme.colors.palettes.grayDark[800];
    case 'grayDark-900':
      return theme.colors.palettes.grayDark[900];
    case 'grayDark-950':
      return theme.colors.palettes.grayDark[950];
    case 'light-primary':
      return theme.mode === 'light'
        ? theme.colors.text.primary
        : theme.colors.palettes.grayLight[900];
    case 'light-secondary':
      return theme.mode === 'light'
        ? theme.colors.text.secondary
        : theme.colors.palettes.grayLight[600];
    case 'light-disabled':
      return theme.mode === 'light'
        ? theme.colors.text.disabled
        : theme.colors.palettes.grayLight[400];
    default:
      return theme.colors.text.primary;
  }
};

// Helper function to check if fontSize/lineHeight is responsive
const isResponsive = (
  value: any
): value is {
  sm?: { px: string; rem: string };
  md?: { px: string; rem: string };
  lg?: { px: string; rem: string };
} => {
  return value && typeof value === 'object' && ('sm' in value || 'md' in value || 'lg' in value);
};

// Helper function to get responsive CSS custom properties
const getResponsiveCSS = (responsiveValue: any, property: string): string => {
  if (!isResponsive(responsiveValue)) return '';

  const cssVars: string[] = [];

  if (responsiveValue.sm) {
    cssVars.push(`--${property}-sm: ${responsiveValue.sm.rem};`);
  }
  if (responsiveValue.md) {
    cssVars.push(`--${property}-md: ${responsiveValue.md.rem};`);
  }
  if (responsiveValue.lg) {
    cssVars.push(`--${property}-lg: ${responsiveValue.lg.rem};`);
  }

  return cssVars.join(' ');
};

// Helper function to get typography styles from theme
const getTypographyStyles = (
  variant: TypographyVariant,
  theme: ReturnType<typeof useThemeStyles>
): React.CSSProperties => {
  const typography = theme.typography;

  // Handle display variants
  if (variant.startsWith('display-')) {
    const displayVariant = variant.replace('display-', '') as DisplayVariant;
    if (displayVariant in typography.display) {
      const styles = typography.display[displayVariant];

      // Check if responsive
      if (isResponsive(styles.fontSize)) {
        return {
          fontSize: 'var(--font-size-md)', // Default to md
          lineHeight: isResponsive(styles.lineHeight)
            ? 'var(--line-height-md)'
            : styles.lineHeight.rem,
          letterSpacing: styles.letterSpacing,
          fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
          // Add CSS custom properties for responsive values
          ['--font-size-sm' as any]: styles.fontSize.sm?.rem,
          ['--font-size-md' as any]: styles.fontSize.md?.rem,
          ['--line-height-sm' as any]: (styles.lineHeight as any).sm?.rem,
          ['--line-height-md' as any]: (styles.lineHeight as any).md?.rem,
        };
      }

      return {
        fontSize: (styles.fontSize as any).rem,
        lineHeight: (styles.lineHeight as any).rem,
        letterSpacing: styles.letterSpacing,
        fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
      };
    }
  }

  // Handle text variants
  if (variant.startsWith('text-')) {
    const textVariant = variant.replace('text-', '') as TextVariant;
    if (textVariant in typography.text) {
      const styles = typography.text[textVariant];

      // Check if responsive
      if (isResponsive(styles.fontSize)) {
        return {
          fontSize: 'var(--font-size-md)', // Default to md
          lineHeight: isResponsive(styles.lineHeight)
            ? 'var(--line-height-md)'
            : styles.lineHeight.rem,
          fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
          // Add CSS custom properties for responsive values
          ['--font-size-sm' as any]: styles.fontSize.sm?.rem,
          ['--font-size-md' as any]: styles.fontSize.md?.rem,
          ['--line-height-sm' as any]: (styles.lineHeight as any).sm?.rem,
          ['--line-height-md' as any]: (styles.lineHeight as any).md?.rem,
        };
      }

      return {
        fontSize: (styles.fontSize as any).rem,
        lineHeight: (styles.lineHeight as any).rem,
        fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
      };
    }
  }

  // Handle direct variants (backward compatibility)
  if (variant in typography.display) {
    const displayVariant = variant as DisplayVariant;
    const styles = typography.display[displayVariant];

    if (isResponsive(styles.fontSize)) {
      return {
        fontSize: 'var(--font-size-md)',
        lineHeight: isResponsive(styles.lineHeight)
          ? 'var(--line-height-md)'
          : styles.lineHeight.rem,
        letterSpacing: styles.letterSpacing,
        fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
        ['--font-size-sm' as any]: (styles.fontSize as any).sm?.rem,
        ['--font-size-md' as any]: (styles.fontSize as any).md?.rem,
        ['--line-height-sm' as any]: (styles.lineHeight as any).sm?.rem,
        ['--line-height-md' as any]: (styles.lineHeight as any).md?.rem,
      };
    }

    return {
      fontSize: (styles.fontSize as any).rem,
      lineHeight: (styles.lineHeight as any).rem,
      letterSpacing: styles.letterSpacing,
      fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
    };
  }

  if (variant in typography.text) {
    const textVariant = variant as TextVariant;
    const styles = typography.text[textVariant];

    // Special handling for text-md responsive variant
    if (textVariant === 'md' && isResponsive(styles.fontSize)) {
      const responsiveFontSize = styles.fontSize as any;
      const responsiveLineHeight = styles.lineHeight as any;

      return {
        fontSize: 'var(--font-size-md)',
        lineHeight: 'var(--line-height-md)',
        fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
        ['--font-size-sm' as any]: responsiveFontSize.sm?.rem,
        ['--font-size-md' as any]: responsiveFontSize.md?.rem,
        ['--line-height-sm' as any]: responsiveLineHeight.sm?.rem,
        ['--line-height-md' as any]: responsiveLineHeight.md?.rem,
      };
    }

    if (isResponsive(styles.fontSize)) {
      return {
        fontSize: 'var(--font-size-md)',
        lineHeight: isResponsive(styles.lineHeight)
          ? 'var(--line-height-md)'
          : (styles.lineHeight as any).rem,
        fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
        ['--font-size-sm' as any]: (styles.fontSize as any).sm?.rem,
        ['--font-size-md' as any]: (styles.fontSize as any).md?.rem,
        ['--line-height-sm' as any]: (styles.lineHeight as any).sm?.rem,
        ['--line-height-md' as any]: (styles.lineHeight as any).md?.rem,
      };
    }

    return {
      fontSize: (styles.fontSize as any).rem,
      lineHeight: (styles.lineHeight as any).rem,
      fontWeight: typography.fontWeight[styles.fontWeight as TypographyWeight],
    };
  }

  return {};
};

// Main Typography component
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      children,
      variant = 'md',
      as,
      color = 'primary',
      weight,
      family,
      align,
      transform,
      decoration,
      className,
      style,
      truncate = false,
      noWrap = false,
      selectable = true,
      margin,
      padding,
      customColor,
      lineHeight,
      letterSpacing,
      fontSize,
      ...props
    },
    ref
  ) => {
    const theme = useThemeStyles();

    // Determine the HTML element to render
    const getElement = (): TypographyElement => {
      if (as) return as;

      // Auto-determine element based on variant
      if (variant.startsWith('display')) {
        const size = variant.replace('display', '');
        switch (size) {
          case '2xl':
          case 'xl':
            return 'h1';
          case 'lg':
          case 'md':
            return 'h2';
          case 'sm':
          case 'xs':
            return 'h3';
          default:
            return 'h1';
        }
      }

      return 'p';
    };

    const Element = getElement();

    // Build class names
    const classes = [
      styles.typography,
      styles[`variant-${variant}`],
      getResponsiveClasses(color, 'color'),
      getResponsiveClasses(weight, 'weight'),
      getResponsiveClasses(family, 'family'),
      getResponsiveClasses(align, 'align'),
      getResponsiveClasses(transform, 'transform'),
      getResponsiveClasses(decoration, 'decoration'),
      truncate && styles.truncate,
      noWrap && styles.noWrap,
      !selectable && styles.noSelect,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Build inline styles
    const inlineStyles: React.CSSProperties = {
      ...getTypographyStyles(variant, theme),
      ...style,
    };

    // Add color
    if (customColor) {
      inlineStyles.color = customColor;
    } else if (typeof color === 'string') {
      inlineStyles.color = getColorValue(color, theme);
    }

    // Add font family
    if (family && typeof family === 'string') {
      inlineStyles.fontFamily = theme.typography.fontFamily[family];
    }

    // Add font weight
    if (weight && typeof weight === 'string') {
      inlineStyles.fontWeight = theme.typography.fontWeight[weight];
    }

    // Add text alignment
    if (align && typeof align === 'string') {
      inlineStyles.textAlign = align;
    }

    // Add text transform
    if (transform && typeof transform === 'string') {
      inlineStyles.textTransform = transform;
    }

    // Add text decoration
    if (decoration && typeof decoration === 'string') {
      inlineStyles.textDecoration = decoration;
    }

    // Add spacing
    if (margin && typeof margin === 'string') {
      inlineStyles.margin = margin;
    }
    if (padding && typeof padding === 'string') {
      inlineStyles.padding = padding;
    }

    // Add overrides - these should have higher priority
    if (lineHeight && typeof lineHeight === 'string') {
      (inlineStyles as any)['--custom-line-height'] = lineHeight;
    }
    if (letterSpacing && typeof letterSpacing === 'string') {
      (inlineStyles as any)['--custom-letter-spacing'] = letterSpacing;
    }
    if (fontSize && typeof fontSize === 'string') {
      (inlineStyles as any)['--custom-font-size'] = fontSize;
    }

    return React.createElement(
      Element,
      {
        ref,
        className: classes,
        style: inlineStyles,
        ...props,
      },
      children
    );
  }
);

Typography.displayName = 'Typography';

// Convenience components for common use cases
export const Heading1 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-2xl" as="h1" {...props} />
);

export const Heading2 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-xl" as="h2" {...props} />
);

export const Heading3 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-lg" as="h3" {...props} />
);

export const Heading4 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-md" as="h4" {...props} />
);

export const Heading5 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-sm" as="h5" {...props} />
);

export const Heading6 = forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="display-xs" as="h6" {...props} />
);

export const BodyLarge = forwardRef<HTMLParagraphElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="text-xl" as="p" {...props} />
);

export const Body = forwardRef<HTMLParagraphElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="text-md" as="p" {...props} />
);

export const BodySmall = forwardRef<HTMLParagraphElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="text-sm" as="p" {...props} />
);

export const Caption = forwardRef<HTMLElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="text-xs" as="span" {...props} />
);

export const Label = forwardRef<HTMLLabelElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => <Typography ref={ref} variant="text-sm" as="label" weight="medium" {...props} />
);

export const Code = forwardRef<HTMLElement, Omit<TypographyProps, 'variant' | 'as'>>(
  (props, ref) => (
    <Typography
      ref={ref}
      variant="text-sm"
      as="code"
      family="secondary"
      style={{
        backgroundColor: 'rgba(0,0,0,0.1)',
        padding: '0.125rem 0.25rem',
        borderRadius: '0.25rem',
      }}
      {...props}
    />
  )
);

Heading1.displayName = 'Heading1';
Heading2.displayName = 'Heading2';
Heading3.displayName = 'Heading3';
Heading4.displayName = 'Heading4';
Heading5.displayName = 'Heading5';
Heading6.displayName = 'Heading6';
BodyLarge.displayName = 'BodyLarge';
Body.displayName = 'Body';
BodySmall.displayName = 'BodySmall';
Caption.displayName = 'Caption';
Label.displayName = 'Label';
Code.displayName = 'Code';
